import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { CalendarDays, MapPin, Users } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import StageEntryAction from './StageEntryAction';
import EnteredAthletes from '../guardian/EnteredAthletes';
import { hasWardToEnter, wardsEnteredIn } from '../guardian/useGuardianWards';
import type { HeldWard } from '../../services/guardianships';
import type { Stage, StageParticipation, TrophyParticipation } from '../../types/trophy';
import { competitionBadge } from '../../utils/lifecycle-status';

function formatDate(value?: string | null) {
  if (!value) return null;
  return new Date(value).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

interface TrophyStagesListProps {
  stages: Stage[];
  /** Where the viewer stands at each stage; without it the list is just a list. */
  participation?: TrophyParticipation;
  signedIn?: boolean;
  onRequestEnter?: (stage: StageParticipation) => void;
  /** The stage whose button is waiting on the question behind it, if any. */
  busyStageId?: string | null;
  /**
   * The children this account holds: each stage card shows the ones that are
   * on its start list, so a parent can see — and undo — what they entered.
   */
  wards?: HeldWard[];
  onWardsChanged?: () => void;
}

/**
 * The circuit's stages, where a gym's profile has its walls.
 *
 * Each card is a link into the stage's own page — which is a competition page,
 * because a stage *is* one: rounds, judging and results all already work there,
 * and nothing about them needed to learn what a circuit is.
 *
 * Beside each card, where the viewer stands: in it, or one tap from being in.
 * Kept outside the link on purpose — a button inside an anchor is a tap that
 * does two things.
 */
export default function TrophyStagesList({
  stages,
  participation,
  signedIn = false,
  onRequestEnter,
  busyStageId = null,
  wards = [],
  onWardsChanged,
}: TrophyStagesListProps) {
  const { t } = useTranslation();
  const standingById = new Map(participation?.stages.map((stage) => [stage.id, stage]) ?? []);

  if (stages.length === 0) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-sm text-muted-foreground">
          {t('trophyProfile.noStages')}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      <h2 className="px-1 text-sm font-semibold text-muted-foreground">
        {t('trophyProfile.stages', { count: stages.length })}
      </h2>

      {stages.map((stage, index) => {
        const standing = standingById.get(stage.id);
        // The badge says what the row can actually offer: a stage held up by
        // its organiser is not "entries open" (see StageEntryAction).
        const badge = competitionBadge(stage.status, stage.entries_open, !!standing?.entry_block);

        return (
          <Card
            key={stage.id}
            className="overflow-hidden transition-colors hover:border-primary/50"
            data-testid="trophy-stage"
            data-entered={standing?.entered ? 'true' : undefined}
          >
            <CardContent className="p-0">
             <div className="flex items-stretch gap-0">
              <Link to={`/competitions/${stage.slug}`} className="flex min-w-0 flex-1 items-stretch">
                {stage.cover_image_small_url ? (
                  <img
                    src={stage.cover_image_small_url}
                    alt=""
                    className="hidden w-32 shrink-0 object-cover sm:block"
                    loading="lazy"
                    decoding="async"
                  />
                ) : (
                  <div className="hidden w-32 shrink-0 items-center justify-center bg-muted sm:flex">
                    <span className="text-2xl font-bold text-muted-foreground">{index + 1}</span>
                  </div>
                )}

                <div className="min-w-0 flex-1 space-y-2 p-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="truncate font-semibold">{stage.name}</span>
                    <Badge variant={badge.variant}>{t(badge.labelKey)}</Badge>
                  </div>

                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                    {stage.start_date && (
                      <span className="flex items-center gap-1">
                        <CalendarDays className="h-3 w-3" aria-hidden />
                        {formatDate(stage.start_date)}
                        {stage.end_date && ` — ${formatDate(stage.end_date)}`}
                      </span>
                    )}
                    {(stage.venue?.name || stage.city) && (
                      <span className="flex items-center gap-1">
                        <MapPin className="h-3 w-3" aria-hidden />
                        {stage.venue?.name ?? stage.city}
                      </span>
                    )}
                    {stage.participants_count > 0 && (
                      <span className="flex items-center gap-1">
                        <Users className="h-3 w-3" aria-hidden />
                        {t('trophyProfile.entrants', { count: stage.participants_count })}
                      </span>
                    )}
                  </div>
                </div>
              </Link>

              {standing && onRequestEnter && (
                <div className="flex shrink-0 items-center pr-4">
                  <StageEntryAction
                    stage={standing}
                    signedIn={signedIn}
                    busy={busyStageId === standing.id}
                    othersToEnter={hasWardToEnter(wards, stage.slug)}
                    onRequestEnter={onRequestEnter}
                  />
                </div>
              )}
             </div>

              {/* The children of this account who are on this stage's start
                  list — a managed account has no page of its own to see it on. */}
              <EnteredAthletes
                entered={wardsEnteredIn(wards, stage.slug)}
                competitionSlug={stage.slug}
                onWithdrawn={() => onWardsChanged?.()}
                className="border-t px-4 py-3"
              />
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
